<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import { isPictureChoiceField } from "@/util/pictureChoice";
import { XCloseIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIDrawer, UITabPane, UITabs } from "@gohighlevel/ghl-ui";
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import FormCustomSidebar from "../../forms/FormCustomSidebar.vue";
import QuizElementSidebar from "../../quizzes/QuizElementSidebar.vue";
import SurveyCustomSidebar from "../../surveys/SurveyCustomSidebar.vue";
import Themes from "../Themes.vue";
import AdvanceSettings from "./AdvanceSettings.vue";
import SidebarOptions from "./OptionsSidebar.vue";
import SidebarStyles from "./Styles.vue";
const { t } = useI18n();

const store = useAppStore();
const emitter = useEmitter();

const formSidebarComponentKey = ref(0);
const surveySidebarComponentKey = ref(0);
const QuizElementSidebarKey = ref(0);
const forceRerenderForm = () => {
  formSidebarComponentKey.value += 1;
};
const forceRerenderSurvey = () => {
  surveySidebarComponentKey.value += 1;
};
const forceRerenderQuiz = () => {
  QuizElementSidebarKey.value += 1;
};

const show = ref(false);

const toggleOverlay = () => {
  show.value = !show.value;
  store.lastStatusOfRightDrawer = !store.lastStatusOfRightDrawer;
};

const closeOverlay = () => {
  show.value = false;
  store.lastStatusOfRightDrawer = false;
};

// Handle sidebar state requests
const getSidebarState = (callback) => {
  if (typeof callback === "function") {
    callback({
      rightSidebarOpen: show.value,
      // Pass existing state plus this sidebar's state
    });
  }
};

// Add direct state access for improved state management
const getRightSidebarState = (callback) => {
  if (typeof callback === "function") {
    callback(show.value);
  }
};

// Close sidebar on request
const closeAllSidebars = () => {
  show.value = false;
  store.lastStatusOfRightDrawer = false;
};

// Restore sidebar state
const restoreSidebarState = (state) => {
  if (state && typeof state.rightSidebarOpen === "boolean") {
    show.value = state.rightSidebarOpen;
    store.lastStatusOfRightDrawer = state.rightSidebarOpen;
  }
};

const closeAdvanceSettings = () => {
  store.isAdvanceSettingEnable = false;
  show.value = true;
};
const openAdvanceSettings = () => {
  store.isAdvanceSettingEnable = true;
  show.value = false;
};

// Normalize sidebar selection for surveys to always use indices
const normalizedSurveySelection = computed(() => {
  const sel: any = store.sidebarOverlaySettings as any;
  if (!sel) {
    return sel;
  }
  // If already index/slideIndex based (surveys default), return as-is
  if (Object.prototype.hasOwnProperty.call(sel, "slideIndex")) {
    return sel;
  }
  // Convert id-based selection (used by some helpers) to indices for SurveyCustomSidebar
  const slideId = sel?.slideId;
  const elementId = sel?.elementId;
  const slideIndex = store.slides?.findIndex((s: any) => s?.id === slideId);
  let index: number | undefined = undefined;
  if (typeof slideIndex === "number" && slideIndex >= 0 && elementId) {
    index = (store.slides[slideIndex].slideData || []).findIndex(
      (el: any) => el?.uuid === elementId
    );
    if (index < 0) {
      index = undefined;
    }
  }
  return { index, slideIndex, formIndex: sel?.formIndex } as any;
});

const showQuizPictureChoiceSidebar = computed(() => {
  if (!store.quizId) return false;

  const selection = normalizedSurveySelection.value;
  if (
    typeof selection?.slideIndex !== "number" ||
    typeof selection?.index !== "number"
  ) {
    return false;
  }

  return isPictureChoiceField(
    store.slides?.[selection.slideIndex]?.slideData?.[selection.index]
  );
});

onBeforeMount(() => {
  emitter.on("toggle-right-side-bar", toggleOverlay);

  // Add handlers for sidebar state management
  emitter.on("get-sidebar-state", getSidebarState);
  emitter.on("get-right-sidebar-state", getRightSidebarState);
  emitter.on("close-all-sidebars", closeAllSidebars);
  emitter.on("restore-sidebar-state", restoreSidebarState);
});

onBeforeUnmount(() => {
  emitter.off("toggle-right-side-bar", toggleOverlay);

  // Clean up new handlers
  emitter.off("get-sidebar-state", getSidebarState);
  emitter.off("get-right-sidebar-state", getRightSidebarState);
  emitter.off("close-all-sidebars", closeAllSidebars);
  emitter.off("restore-sidebar-state", restoreSidebarState);
});

watch(
  [
    () => store.isAnyElementActive,
    () => store.currentCustomSidebarPreview,
    () => store.currentActiveIndex,
  ],
  ([newIsActive], [oldIsActive]) => {
    if (newIsActive) {
      show.value = false;
    } else if (store.lastStatusOfRightDrawer) {
      show.value = true;
    }
  }
);
// Reload Sidebar element settings
watch(
  () => store.currentCustomSidebarPreview,
  (newData, oldData) => {
    forceRerenderForm();
  }
);
watch(
  () => store.sidebarOverlaySettings,
  (newData, oldData) => {
    forceRerenderSurvey();
    forceRerenderQuiz();
  }
);
</script>

<template>
  <div>
    <div>
      <UIDrawer
        id="hl-builder-element-layout"
        :show="show"
        :width="360"
        placement="right"
        :show-mask="false"
        to="#builder-inner-container"
        display-directive="show"
        class="border-gray-100"
      >
        <div class="flex w-full justify-between p-2">
          <UIButton
            id="hl-builder-creator-header-close"
            :quaternary="true"
            text
            :aria-label="t('common.close')"
            @click="closeOverlay"
          >
            <XCloseIcon class="h-5 w-5 text-gray-800" />
          </UIButton>
        </div>
        <div>
          <div id="sidebar-tab" class="sidebar-tab-container">
            <UITabs justify-content="space-evenly" default-value="styles">
              <UITabPane name="styles" :tab="t('common.styles')">
                <SidebarStyles :product="store.product" @open:advance="openAdvanceSettings" />
              </UITabPane>
              <UITabPane v-if="!store.quizId" name="themes" :tab="t('common.themes.themes')">
                <Themes :is-survey="store.surveyId ? true : false" />
              </UITabPane>
              <UITabPane
                name="options"
                :tab="store.isNewBuilderLayoutEnabled ? t('common.advanced') : t('common.options')"
              >
                <template v-if="!store.isNewBuilderLayoutEnabled">
                  <SidebarOptions :product="store.product" />
                </template>
                <template v-else>
                  <AdvanceSettings />
                </template>
              </UITabPane>
            </UITabs>
          </div>
        </div>
      </UIDrawer>
    </div>
  </div>
  <div>
    <div v-if="store.isAnyElementActive" class="sidebar-right">
      <FormCustomSidebar
        v-if="store.formId"
        id="form_custom_sidebar"
        :key="formSidebarComponentKey"
        :selected-index="store.currentCustomSidebarPreview"
      ></FormCustomSidebar>
      <SurveyCustomSidebar
        v-else-if="store.surveyId || showQuizPictureChoiceSidebar"
        id="survey_custom_sidebar"
        :key="surveySidebarComponentKey"
        :selected-element="normalizedSurveySelection"
      >
      </SurveyCustomSidebar>
      <QuizElementSidebar
        v-else
        id="quiz_element_sidebar"
        :key="QuizElementSidebarKey"
        :selected-element="store.sidebarOverlaySettings"
      ></QuizElementSidebar>
    </div>
    <div
      v-if="store.isAdvanceSettingEnable && !store.isNewBuilderLayoutEnabled"
      class="advance-settings-container"
    >
      <AdvanceSettings @back-to-normal-settings="closeAdvanceSettings"></AdvanceSettings>
    </div>
  </div>
</template>

<style scoped>
.glh-search {
  position: relative;
}
.glh-search i {
  position: absolute;
  right: 15px;
  top: 18px;
}
.sidebar-tab-container {
  font-size: 14px;
}
#form_custom_sidebar,
#survey_custom_sidebar {
  overflow-y: scroll;
  scrollbar-width: none;
  height: 95vh;
  padding: 15px;
  padding-bottom: v-bind("store.isNewBuilderLayoutEnabled ? '180px' : '60px'");
  user-select: none;
  -webkit-user-select: none;
}

#quiz_element_sidebar {
  overflow-y: scroll;
  scrollbar-width: none;
  height: 88vh;
  padding: 15px;
  padding-bottom: v-bind("store.isNewBuilderLayoutEnabled ? '180px' : '60px'");
  user-select: none;
  -webkit-user-select: none;
}
#quiz_element_sidebar {
  padding: 0;
}
#form_custom_sidebar::-webkit-scrollbar {
  display: none;
}
#survey_custom_sidebar::-webkit-scrollbar,
#quiz_element_sidebar::-webkit-scrollbar {
  display: none;
}
#sidebar-tab .n-tabs-nav {
  width: 100%;
  z-index: 1;
  background: #ffffff;
  left: 0;
}
.close-icon-element {
  right: 18px;
  top: 18px;
  position: absolute;
  cursor: pointer;
}
.sidebar-right {
  position: fixed;
  right: 0;
  top: 98px;
  width: 100%;
  max-width: 360px;
  background-color: #ffffff;
  border: 1px solid #dee2e6 !important;
  height: 100%;
  z-index: 900; /* above canvas, below header */
}

.advance-settings-container {
  position: fixed;
  right: 0;
  top: 98px;
  width: 100%;
  max-width: 360px;
  background-color: #ffffff;
  border: 1px solid #dee2e6 !important;
  height: 100%;
  overflow-y: scroll;
  scrollbar-width: none;
  height: 90vh;
  z-index: 900;
}
</style>
